import { useNavigate } from "react-router-dom";
import { OctagonAlert } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { usePermission } from "@/hooks/userPermission";
import { PERMISSIONS } from "@/constants";
import { useAppSelector } from "@/hooks/redux";
import { useMixpanel } from "@/hooks/useMixpanel";
import { useTranslation } from "react-i18next";

export default function PermissionDialog() {
  const navigate = useNavigate();
  const { trackEvent } = useMixpanel();
  const { t } = useTranslation();
  const { permitted, permissionName, closeDialog } = usePermission();
  const { currentPlan } = useAppSelector((state) => state.purchase);
  const getSubscriptionDescription = (name: string) => {
    if (!name) return null;
    const planName = currentPlan?.slice(0, -5) || "";
    const isStandard = planName === "go2work Standard";
    const slots = isStandard ? 1 : 3;
    const slotLabel = isStandard ? "one" : "three";
    const limit = isStandard ? 25 : 100;

    return t(`permission_dialog.descriptions.${name}`, {
      plan: planName,
      slots,
      slotLabel,
      limit,
    });
  };

  return (
    <Dialog open={!permitted} onOpenChange={closeDialog}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <div className="flex flex-col items-center justify-center gap-8 py-4 text-center">
            <span className="mt-4 flex h-20 w-20 rounded-full bg-destructive p-3 drop-shadow-lg">
              <OctagonAlert className="h-full w-full text-white" />
            </span>
            <div>
              <DialogTitle className="mb-1 text-xl">{t("permission_dialog.title")}</DialogTitle>
              <DialogDescription>{getSubscriptionDescription(permissionName)}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogFooter className="flex flex-col-reverse gap-4 md:flex-row">
          <Button
            variant="outline"
            onClick={() => {
              closeDialog();
              trackEvent("Permission Dialog: Cancel Button Clicked", {
                permission: permissionName,
                currentPlan,
                component: "Permission Dialog",
              });
            }}
          >
            {t("permission_dialog.buttons.cancel")}
          </Button>
          <Button
            onClick={() => {
              closeDialog();
              navigate("/employer/subscription");
              trackEvent("Permission Dialog: Upgrade Button Clicked", {
                permission: permissionName,
                currentPlan,
                component: "Permission Dialog",
              });
            }}
          >
            {t("permission_dialog.buttons.upgrade")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
